import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

// Compose + send from the signed-in user's own Gmail.
//
//   mode 'new'      — a new conversation (the offer). Preselects the latest generated purchase contract.
//   mode 'reply'    — continue `threadId` (subject is the thread's "Re: …").
//   mode 'followup' — send a due reminder's draft; `followup` is the email_followups row.
//
// Nothing is sent until the user presses Send. "Remind me" only schedules a reminder: when it comes
// due, feebe drafts the next email and waits for the user again. A reply from the agent cancels it.

const REMIND_OPTIONS = [
  { v: '', l: 'No reminder' },
  { v: '1', l: 'Tomorrow' },
  { v: '2', l: 'In 2 days' },
  { v: '3', l: 'In 3 days' },
  { v: '5', l: 'In 5 days' },
  { v: '7', l: 'In a week' },
  { v: 'custom', l: 'Pick a date…' },
];

function parseEmail(text) {
  if (!text) return { subject: '', body: '' };
  const m = text.match(/^Subject:\s*(.+?)\n\n([\s\S]+)$/);
  return m ? { subject: m[1].trim(), body: m[2].trim() } : { subject: '', body: text };
}

// Due at 9am local on the chosen day — a reminder at 2:47am is noise.
function dueFromDays(days) {
  const d = new Date();
  d.setDate(d.getDate() + Number(days));
  d.setHours(9, 0, 0, 0);
  return d.toISOString();
}

const fmtSize = (n) => (n > 1048576 ? `${(n / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round((n || 0) / 1024))} KB`);
const DOC_LABEL = {
  generated_purchase_contract: 'Offer contract', generated_assignment: 'Assignment',
  executed_purchase_contract: 'Executed contract',
};

export default function GmailComposeModal({ deal, mode = 'new', threadId = null, followup = null, initialTo, initialSubject, initialBody, preselectFilename, onClose, onSent }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [conn, setConn] = useState(null);
  const [docs, setDocs] = useState([]);
  const [picked, setPicked] = useState(new Set());
  // Reusable attachments (proof of funds, disclosures) — a SEPARATE id space from deal documents,
  // so they get their own picked-set and are never merged into one list (migration 081).
  const [library, setLibrary] = useState([]);
  const [pickedLib, setPickedLib] = useState(new Set());
  const [showLibrary, setShowLibrary] = useState(false);
  const [templates, setTemplates] = useState([]);
  // Mappable offer documents (084): a THIRD id space. These are not files yet — each is generated
  // for this deal at Send, so what is ticked here is a template id, not a document id.
  const [offerDocs, setOfferDocs] = useState([]);
  const [pickedOffer, setPickedOffer] = useState(new Set());
  const [to, setTo] = useState(initialTo ?? followup?.to_email ?? deal?.agent_email ?? '');
  const [cc, setCc] = useState('');
  const [showCc, setShowCc] = useState(false);
  const [subject, setSubject] = useState(initialSubject || (mode === 'new' && deal?.address ? `Offer for ${deal.address}` : ''));
  const [body, setBody] = useState(initialBody || '');
  const [remind, setRemind] = useState(mode === 'reply' ? '' : '3');
  const [customDate, setCustomDate] = useState('');
  const [drafting, setDrafting] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  useEffect(() => {
    fetch('/api/integrations/gmail/status').then(r => r.json()).then(setConn).catch(() => setConn({ connected: false }));
    fetch(`/api/documents?deal_id=${deal.id}`).then(r => r.json()).then(d => {
      const list = Array.isArray(d?.documents) ? d.documents : [];
      setDocs(list);
      // Preselect the contract this email is about: the one just generated, else the newest offer.
      const newest = (xs) => xs.sort((a, b) => new Date(b.created_at) - new Date(a.created_at))[0];
      const byName = preselectFilename && newest(list.filter(x => x.filename === preselectFilename));
      const offer = byName || (mode === 'new' && newest(list.filter(x => x.doc_type === 'generated_purchase_contract')));
      if (offer) setPicked(new Set([offer.id]));
    }).catch(() => setDocs([]));
    fetch('/api/library-documents').then(r => r.json())
      .then(d => setLibrary(Array.isArray(d?.documents) ? d.documents : [])).catch(() => setLibrary([]));
    fetch('/api/email-templates').then(r => r.json())
      .then(d => setTemplates(Array.isArray(d?.templates) ? d.templates : [])).catch(() => setTemplates([]));
    fetch('/api/contracts/templates').then(r => r.json())
      .then(d => setOfferDocs(Array.isArray(d) ? d.filter(t => t.kind === 'supplemental' && t.exists) : []))
      .catch(() => setOfferDocs([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deal.id]);

  // A follow-up opens with its draft already written.
  useEffect(() => {
    if (mode === 'followup' && followup && !initialBody) draft(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function draft(regenerate = true) {
    setDrafting(true); setError(null);
    try {
      if (mode === 'followup') {
        const r = await fetch(`/api/gmail/followups/${followup.id}/draft${regenerate ? '?regenerate=1' : ''}`);
        const d = await r.json();
        if (!r.ok) throw new Error(d.error || 'Draft failed');
        setSubject(d.subject || ''); setBody(d.body || '');
        if (d.to && !to) setTo(d.to);
      } else {
        const r = await fetch(`/api/deals/${deal.id}/outreach`, { method: 'POST' });
        const d = await r.json();
        if (!r.ok || d.error) throw new Error(d.error || 'Draft failed');
        const p = parseEmail(d.email);
        if (mode === 'new') setSubject(p.subject);
        setBody(p.body);
      }
    } catch (e) { setError(e.message); } finally { setDrafting(false); }
  }

  // Fill a saved template into the compose box. Placeholders are substituted from the deal so a
  // template reads as a written email rather than a form; an unknown token is left alone rather than
  // blanked, so a typo is visible instead of silently deleting text.
  function applyTemplate(t) {
    if (!t) return;
    const subs = {
      address: deal.address || '', city: deal.city || '', zip: deal.zip || '',
      agent_name: deal.agent_name || 'there',
      offer: deal.offer_amount ? `$${Number(deal.offer_amount).toLocaleString()}` : '',
      my_name: user?.name || '',
    };
    const fill = (txt) => String(txt || '').replace(/\{\{\s*(\w+)\s*\}\}/g,
      (m, k) => (Object.prototype.hasOwnProperty.call(subs, k) ? subs[k] : m));
    // Never overwrite a REPLY's subject — the thread owns it (the field is disabled in that mode).
    if (mode === 'new' && t.subject) setSubject(fill(t.subject));
    setBody(fill(t.body));
  }

  const dueAt = useMemo(() => {
    if (!remind) return null;
    if (remind === 'custom') return customDate ? new Date(`${customDate}T09:00:00`).toISOString() : null;
    return dueFromDays(remind);
  }, [remind, customDate]);

  const pickedDocs = docs.filter(d => picked.has(d.id));
  const pickedLibs = library.filter(d => pickedLib.has(d.id));
  // ONE budget across both stores — Gmail's cap is on the message, not per store (deal-mail.js
  // enforces the same sum server-side).
  // Offer documents do not exist until Send, so they add to the COUNT but not to the measured size.
  // The server re-checks the 18MB budget against the real bytes, so this figure is a hint and never
  // the gate — and the label says so when a not-yet-generated document is selected, rather than
  // presenting a partial total as if it were the final one.
  const attachCount = pickedDocs.length + pickedLibs.length + pickedOffer.size;
  const attachBytes = [...pickedDocs, ...pickedLibs].reduce((n, d) => n + Number(d.byte_size || 0), 0);
  const sizeIsPartial = pickedOffer.size > 0;
  const tooBig = attachBytes > 18 * 1048576;
  const canSend = conn?.connected && to.trim() && body.trim() && (mode !== 'new' || subject.trim()) && !tooBig && !sending && (remind !== 'custom' || customDate);

  async function send() {
    setSending(true); setError(null);
    const followUp = dueAt ? { due_at: dueAt } : null;
    try {
      // Fill the ticked offer documents for THIS deal first, then attach what comes back. Generating
      // at Send rather than in advance is what guarantees the disclosure carries this property's
      // address — a document generated when it was uploaded would be right exactly once.
      let generatedIds = [];
      if (pickedOffer.size) {
        const g = await fetch('/api/contracts/offer-documents/generate', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ deal_id: deal.id, template_ids: [...pickedOffer] }),
        });
        const gd = await g.json().catch(() => ({}));
        if (!g.ok) {
          // Name the document AND the fields, so the fix is obvious without opening Settings to guess.
          const detail = (gd.documents || [])
            .map(x => `${x.name || 'Document'}${x.missing_fields?.length ? `: ${x.missing_fields.map(f => f.label || f).join(', ')}` : ''}`)
            .join(' · ');
          throw new Error(detail ? `${gd.error || 'Could not prepare the offer documents'} — ${detail}` : (gd.error || 'Could not prepare the offer documents'));
        }
        generatedIds = (gd.documents || []).map(d => d.id);
      }
      const payload = {
        to, cc: showCc ? cc : '', subject, body,
        document_ids: [...picked, ...generatedIds],
        library_document_ids: [...pickedLib],
      };
      const url = mode === 'followup' ? `/api/gmail/followups/${followup.id}/send` : `/api/gmail/deals/${deal.id}/send`;
      const r = await fetch(url, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(mode === 'followup'
          ? { ...payload, next_follow_up: followUp }
          : { ...payload, thread_id: threadId, follow_up: followUp }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Send failed');
      setResult({ ...d, dueAt });
      onSent && onSent(d);
    } catch (e) { setError(e.message); } finally { setSending(false); }
  }

  const title = mode === 'followup' ? 'Follow up' : mode === 'reply' ? 'Reply' : 'Email the agent';

  return (
    <div style={overlay} onMouseDown={e => { if (e.target === e.currentTarget && !sending) onClose(); }}>
      <div style={panel}>
        <div style={{ padding: '18px 24px 12px', borderBottom: '1px solid var(--navy-border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--white)' }}>{title}</div>
            <div style={{ fontSize: 11.5, color: 'var(--gray-500)', marginTop: 2 }}>
              {deal.address}{conn?.email ? <> · from <span style={{ color: 'var(--teal)' }}>{conn.email}</span></> : ''}
            </div>
          </div>
          <button onClick={onClose} disabled={sending} style={{ background: 'none', border: 'none', color: 'var(--gray-500)', fontSize: 22, cursor: 'pointer', lineHeight: 1 }}>×</button>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '16px 24px 22px' }}>
          {conn === null ? (
            <div style={{ padding: '40px 0', textAlign: 'center', color: 'var(--gray-500)', fontSize: 13 }}>Loading…</div>
          ) : !conn.connected ? (
            <div style={{ padding: '28px 4px', textAlign: 'center' }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--white)', marginBottom: 6 }}>
                {conn.status === 'revoked' ? 'Reconnect your Gmail' : 'Connect your Gmail to send from feebe'}
              </div>
              <div style={{ fontSize: 12.5, color: 'var(--gray-300)', marginBottom: 18, lineHeight: 1.5 }}>
                Offers go out from your own address with the contract attached, and the agent's replies show up on this deal.
              </div>
              <button onClick={() => navigate('/settings')} style={btnPrimary}>Go to Settings</button>
            </div>
          ) : result ? (
            <div>
              <div style={{ padding: '14px 16px', background: result.status === 'sent' ? 'rgba(74,124,89,0.08)' : 'rgba(201,162,39,0.08)', border: `1px solid ${result.status === 'sent' ? 'rgba(74,124,89,0.3)' : 'rgba(201,162,39,0.35)'}`, borderRadius: 10, marginBottom: 14 }}>
                <div style={{ fontSize: 13.5, fontWeight: 700, color: result.status === 'sent' ? '#4A7C59' : 'var(--gold)' }}>
                  {result.status === 'sent' ? `Sent from ${conn.email}` : 'Logged, not sent'}
                </div>
                <div style={{ fontSize: 12, color: 'var(--gray-300)', marginTop: 4, lineHeight: 1.5 }}>
                  {result.status !== 'sent' && (result.reason === 'demo_mode' ? 'Demo accounts never send real email. ' : 'Gmail sending is switched off on this server. ')}
                  {attachCount ? `Attached: ${[...pickedDocs.map(d => d.filename), ...offerDocs.filter(t => pickedOffer.has(t.id)).map(t => t.name), ...pickedLibs.map(d => d.label)].join(', ')}. ` : ''}
                  {result.followup_id
                    ? `We'll remind you on ${new Date(result.dueAt).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })} with a draft ready — unless they reply first.`
                    : result.followup_skipped ? 'No follow-up reminder was set, since nothing was sent.' : ''}
                </div>
              </div>
              <button onClick={onClose} style={{ ...btnPrimary, width: '100%' }}>Done</button>
            </div>
          ) : (
            <>
              <Row label="To" right={!showCc && <span onClick={() => setShowCc(true)} style={{ color: 'var(--teal)', cursor: 'pointer' }}>Cc</span>}>
                <input value={to} onChange={e => setTo(e.target.value)} placeholder="agent@brokerage.com" style={input} />
              </Row>
              {showCc && <Row label="Cc"><input value={cc} onChange={e => setCc(e.target.value)} placeholder="Separate addresses with commas" style={input} /></Row>}
              <Row label="Subject">
                <input value={subject} onChange={e => setSubject(e.target.value)} disabled={mode !== 'new'} placeholder={`Offer for ${deal.address}`} style={{ ...input, opacity: mode !== 'new' ? 0.7 : 1 }} />
              </Row>
              <Row label="Message" right={<span style={{ display: 'inline-flex', gap: 12, alignItems: 'center' }}>
                {templates.length > 0 && (
                  <select value="" onChange={e => { const t = templates.find(x => String(x.id) === e.target.value); applyTemplate(t); e.target.value = ''; }}
                    title="Fill this email from one of your saved templates"
                    style={{ padding: '3px 6px', borderRadius: 5, border: '1px solid var(--navy-border)', background: 'var(--navy)', color: 'var(--teal)', fontSize: 11.5, fontFamily: 'inherit', cursor: 'pointer' }}>
                    <option value="">Use a template…</option>
                    {templates.map(t => <option key={t.id} value={t.id}>{t.name}{t.shared_org ? ' (org)' : ''}</option>)}
                  </select>
                )}
                {mode !== 'reply' && (
                  <span onClick={() => !drafting && draft(true)} style={{ color: 'var(--teal)', cursor: drafting ? 'default' : 'pointer' }}>
                    {drafting ? 'Drafting…' : body ? 'Redraft with AI' : 'Draft with AI'}
                  </span>
                )}
              </span>}>
                <textarea value={body} onChange={e => setBody(e.target.value)} rows={12} placeholder={drafting ? 'Writing a draft…' : 'Write your message, or draft one with AI.'} style={{ ...input, lineHeight: 1.55, resize: 'vertical' }} />
              </Row>

              <Row label={`Attachments${attachCount ? ` · ${fmtSize(attachBytes)}${sizeIsPartial ? '+' : ''}` : ''}`}
                right={sizeIsPartial ? <span style={{ color: 'var(--gray-500)', textTransform: 'none', letterSpacing: 0, fontSize: 11 }}>final size checked when you send</span> : null}>
                {docs.length === 0 ? (
                  <div style={{ fontSize: 12, color: 'var(--gray-500)' }}>No documents on this deal yet. Generate a contract or upload one in Documents.</div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4, maxHeight: 150, overflowY: 'auto' }}>
                    {docs.map(d => (
                      <label key={d.id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, color: 'var(--gray-100)', cursor: 'pointer', padding: '5px 8px', borderRadius: 6, background: picked.has(d.id) ? 'var(--teal-dim)' : 'transparent' }}>
                        <input type="checkbox" checked={picked.has(d.id)} onChange={() => setPicked(prev => { const n = new Set(prev); n.has(d.id) ? n.delete(d.id) : n.add(d.id); return n; })} />
                        <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.filename}</span>
                        {DOC_LABEL[d.doc_type] && <span style={{ fontSize: 10.5, color: 'var(--gray-500)' }}>{DOC_LABEL[d.doc_type]}</span>}
                        <span style={{ fontSize: 10.5, color: 'var(--gray-500)' }}>{fmtSize(d.byte_size)}</span>
                      </label>
                    ))}
                  </div>
                )}
                {/* Reusable files from the sender's library — proof of funds, agency disclosure.
                    Collapsed by default so the deal's own contract stays the obvious attachment,
                    and the count is on the button so a picked document is never hidden behind it. */}
                <div style={{ marginTop: 10 }}>
                  <button type="button" onClick={() => setShowLibrary(v => !v)}
                    style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 10px', borderRadius: 6, border: '1px solid var(--navy-border)', background: 'transparent', color: pickedLibs.length ? 'var(--teal)' : 'var(--gray-300)', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                    <span style={{ fontSize: 13, lineHeight: 1 }}>{showLibrary ? '−' : '+'}</span>
                    Add additional documents
                    {pickedLibs.length > 0 && <span style={{ fontSize: 11, color: 'var(--teal)' }}>· {pickedLibs.length} selected</span>}
                  </button>
                  {showLibrary && (
                    <div style={{ marginTop: 8, padding: '8px 10px', borderRadius: 8, border: '1px solid var(--navy-border)', background: 'var(--navy)' }}>
                      {library.length === 0 && offerDocs.length === 0 ? (
                        <div style={{ fontSize: 12, color: 'var(--gray-500)' }}>
                          Nothing in your document library yet. Add a proof of funds or a disclosure in{' '}
                          <span onClick={() => navigate('/settings')} style={{ color: 'var(--teal)', cursor: 'pointer', textDecoration: 'underline' }}>Settings → Offer Documents</span>.
                        </div>
                      ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, maxHeight: 220, overflowY: 'auto' }}>
                          {/* Mappable documents first — they are filled with this property's details
                              when you press Send, which is worth saying on the row itself. An unmapped
                              one is shown disabled rather than hidden: hiding it makes "where did my
                              disclosure go?" a mystery, and the row says exactly what to do. */}
                          {offerDocs.map(t => {
                            const ready = t.status === 'ready';
                            return (
                              <label key={`t${t.id}`} title={ready ? 'Filled with this property\u2019s details when you send' : 'Map this document\u2019s fields in Settings before it can be used'}
                                style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, color: ready ? 'var(--gray-100)' : 'var(--gray-500)', cursor: ready ? 'pointer' : 'not-allowed', padding: '5px 8px', borderRadius: 6, background: pickedOffer.has(t.id) ? 'var(--teal-dim)' : 'transparent', opacity: ready ? 1 : 0.6 }}>
                                <input type="checkbox" disabled={!ready} checked={pickedOffer.has(t.id)}
                                  onChange={() => setPickedOffer(prev => { const n = new Set(prev); n.has(t.id) ? n.delete(t.id) : n.add(t.id); return n; })} />
                                <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.name}</span>
                                {ready
                                  ? <span style={{ fontSize: 9.5, color: 'var(--teal)', fontWeight: 700, letterSpacing: '0.03em', whiteSpace: 'nowrap' }}>FILLS FOR THIS PROPERTY</span>
                                  : <span onClick={e => { e.preventDefault(); navigate('/settings'); }} style={{ fontSize: 9.5, color: '#B08D57', fontWeight: 700, whiteSpace: 'nowrap', textDecoration: 'underline', cursor: 'pointer' }}>NEEDS FIELDS</span>}
                              </label>
                            );
                          })}
                          {library.map(d => (
                            <label key={`l${d.id}`} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, color: 'var(--gray-100)', cursor: 'pointer', padding: '5px 8px', borderRadius: 6, background: pickedLib.has(d.id) ? 'var(--teal-dim)' : 'transparent' }}>
                              <input type="checkbox" checked={pickedLib.has(d.id)}
                                onChange={() => setPickedLib(prev => { const n = new Set(prev); n.has(d.id) ? n.delete(d.id) : n.add(d.id); return n; })} />
                              <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.label}</span>
                              {d.shared_org && <span style={{ fontSize: 10, color: '#B08D57', fontWeight: 700 }}>ORG</span>}
                              <span style={{ fontSize: 10.5, color: 'var(--gray-500)' }}>{fmtSize(d.byte_size)}</span>
                            </label>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
                {tooBig && <div style={{ fontSize: 11.5, color: '#B85450', marginTop: 6 }}>Gmail can't take more than 18 MB of attachments.</div>}
              </Row>

              <Row label="Follow-up reminder">
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                  <select value={remind} onChange={e => setRemind(e.target.value)} style={{ ...input, width: 'auto' }}>
                    {REMIND_OPTIONS.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
                  </select>
                  {remind === 'custom' && <input type="date" value={customDate} min={new Date().toISOString().slice(0, 10)} onChange={e => setCustomDate(e.target.value)} style={{ ...input, width: 'auto' }} />}
                </div>
                <div style={{ fontSize: 11, color: 'var(--gray-500)', marginTop: 5 }}>
                  {remind ? "We'll draft the follow-up and remind you. Nothing sends until you press Send, and a reply cancels it." : 'No follow-up will be scheduled.'}
                </div>
              </Row>

              {!conn.sending_enabled && (
                <div style={{ fontSize: 11.5, color: 'var(--gold)', marginBottom: 10 }}>Gmail sending is switched off on this server — this will be logged but not sent.</div>
              )}
              {error && <div style={{ fontSize: 12, color: '#B85450', marginBottom: 10 }}>{error}</div>}

              <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
                <button onClick={onClose} style={{ ...btn, flex: 1 }}>Cancel</button>
                <button onClick={send} disabled={!canSend} style={{ ...btnPrimary, flex: 2, opacity: canSend ? 1 : 0.5, cursor: canSend ? 'pointer' : 'default' }}>
                  {sending ? 'Sending…' : `Send${attachCount ? ` with ${attachCount} attachment${attachCount > 1 ? 's' : ''}` : ''}`}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function Row({ label, right, children }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, fontWeight: 700, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>
        <span>{label}</span>{right && <span style={{ textTransform: 'none', letterSpacing: 0, fontSize: 11.5 }}>{right}</span>}
      </div>
      {children}
    </div>
  );
}

const overlay = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)', zIndex: 9000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 };
const panel = { background: 'var(--navy-card)', border: '1px solid var(--navy-border)', borderRadius: 16, width: '100%', maxWidth: 640, maxHeight: '92vh', display: 'flex', flexDirection: 'column' };
const input = { width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid var(--navy-border)', background: 'var(--navy)', color: 'var(--white)', fontSize: 12.5, outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box' };
const btn = { padding: '10px 14px', borderRadius: 8, border: '1px solid var(--navy-border)', background: 'transparent', color: 'var(--gray-300)', fontSize: 13, fontWeight: 700, cursor: 'pointer' };
const btnPrimary = { ...btn, background: 'var(--teal)', border: '1px solid var(--teal)', color: '#fff' };
